import { emptySupportCase, supportCaseForm, validateSupportCaseForm } from "./supportCaseSubmission";

export const emptyHistoryFilters = { status: null, relatedId: null, caseId: null };
const statuses = ["open", "waiting", "investigating", "resolved", "closed"];
const object = value => value !== null && typeof value === "object" && !Array.isArray(value);
const exact = (value, keys) => object(value) && Object.keys(value).length === keys.length &&
	Object.keys(value).every(key => keys.includes(key));
const id = value => typeof value === "string" && value.length > 0 && value.length <= 128 &&
	// eslint-disable-next-line no-control-regex
	! /[/\\\s\u0000-\u001f\u007f]/.test(value) && value !== "." && value !== "..";
const token = (value, max) => typeof value === "string" && value.length > 0 && value.length <= max && !/[^A-Za-z0-9_-]/.test(value);
export const validSupportCursor = value => value === null || token(value, 4096);
const isoDate = value => value === null || (typeof value === "string" &&
	/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value) && Number.isFinite(Date.parse(value)) &&
	new Date(value).toISOString() === (value.length === 20 ? value.replace("Z", ".000Z") : value));

export function supportHistoryFilters(draft) {
	const filters = Object.fromEntries(Object.keys(emptyHistoryFilters).map(key => [key, draft[key]?.trim() || null]));
	if ((filters.status !== null && !statuses.includes(filters.status)) ||
		![filters.relatedId, filters.caseId].every(value => value === null || id(value))) {
		throw new Error("Invalid support history filters.");
	}
	return filters;
}

export function validateSupportHistory(data, scope, filters) {
	const fail = () => { throw new Error("Support history response is invalid or does not match the current scope and filters."); };
	if (!exact(data, ["schemaVersion", "projectId", "actorUid", "filters", "cases", "nextCursor"]) ||
		data.schemaVersion !== 1 || data.projectId !== scope.projectId || data.actorUid !== scope.actorUid ||
		!exact(data.filters, Object.keys(emptyHistoryFilters)) ||
		Object.keys(emptyHistoryFilters).some(key => data.filters[key] !== filters[key]) ||
		!validSupportCursor(data.nextCursor) || !Array.isArray(data.cases) || data.cases.length > 100 ||
		(filters.caseId && (data.cases.length > 1 || data.nextCursor !== null))) fail();
	const ids = new Set();
	const fields = [...Object.keys(emptySupportCase), "id", "revision", "projectId", "createdAt", "updatedAt", "createdBy", "updatedBy"];
	for (const row of data.cases) {
		if (!object(row) || Object.keys(row).some(key => !fields.includes(key)) || !id(row.id) || ids.has(row.id) ||
			row.projectId !== scope.projectId || !Number.isSafeInteger(row.revision) || row.revision < 0 ||
			row.revision >= Number.MAX_SAFE_INTEGER || !isoDate(row.createdAt) || !isoDate(row.updatedAt) ||
			["createdBy", "updatedBy"].some(key => row[key] !== undefined && row[key] !== null && row[key] !== "" && !id(row[key])) ||
			Object.keys(emptySupportCase).some(key => row[key] !== undefined && row[key] !== null && typeof row[key] !== "string") ||
			(filters.status && row.status !== filters.status) || (filters.caseId && row.id !== filters.caseId) ||
			(filters.relatedId && ![row.customerId, row.restaurantId, row.orderId, row.reservationId].includes(filters.relatedId))) fail();
		try { validateSupportCaseForm(supportCaseForm(row)); } catch (_) { fail(); }
		ids.add(row.id);
	}
	return { cases: data.cases, nextCursor: data.nextCursor };
}

export function supportIncidentDraft(incident, projectId) {
	const diagnosticId = value => token(value, 150);
	if (!exact(incident, ["projectId", "restaurantId", "targetType", "targetId", "auditId", "observedAtMs"]) ||
		incident.projectId !== projectId || !diagnosticId(incident.projectId) || !diagnosticId(incident.restaurantId) ||
		incident.restaurantId.length > 128 || !["table", "party"].includes(incident.targetType) ||
		!diagnosticId(incident.targetId) || !diagnosticId(incident.auditId) || !Number.isSafeInteger(incident.observedAtMs) ||
		incident.observedAtMs < 0 || incident.observedAtMs > 8640000000000000) {
		throw new Error("Service diagnostic incident is invalid or belongs to another project.");
	}
	return { ...emptySupportCase, type: "restaurant_ops", restaurantId: incident.restaurantId,
		title: `Service incident: ${incident.targetType} ${incident.targetId}`,
		description: ["Service diagnostic incident", `Project: ${incident.projectId}`, `Restaurant: ${incident.restaurantId}`,
			`Target: ${incident.targetType} ${incident.targetId}`, `Diagnostic audit ID: ${incident.auditId}`,
			`Observed at: ${new Date(incident.observedAtMs).toISOString()}`].join("\n") };
}
